<!-- eslint-disable max-len -->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ConversationIndexCard from '@/components/Conversations/ConversationIndex/ConversationIndexCard.vue';
import ConversationIndexLoaderCards from '@/components/Conversations/ConversationIndex/Loader/ConversationIndexLoaderCards.vue';
import ConversationIndexFilterEmptyResults from '@/components/Conversations/ConversationIndex/Filters/ConversationIndexFilterEmptyResults.vue';
import ConversationIndexStatusFilter from './Filters/ConversationIndexStatusFilter.vue';
import ConversationIndexPopoverFilter from './Filters/ConversationIndexPopoverFilter.vue';
import ConversationIndexSearchFilter from './Filters/ConversationIndexSearchFilter.vue';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';
import ConversationBulkOptions from '@/components/Conversations/ConversationIndex/ConversationBulkOptions.vue';
import RejectedModal from '@/components/Conversations/Application/RejectedModal.vue';
import BulkMessagingModal from '@/components/Conversations/Application/BulkMessageModal.vue';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import GlobalCandidateSearchResults from '@/components/Conversations/ConversationIndex/GlobalSearch/GlobalCandidateSearchResults.vue';
import { useGlobalCandidateSearch } from '@/composables/conversations/useGlobalCandidateSearch';
import { useConversationListInfiniteScroll } from '@/composables/conversations/useConversationListInfiniteScroll';
import { useScrollFades } from '@/composables/useScrollFades';
import { navigateAfterConversationStatusChange } from '@/composables/jobs/useConversationStatusNavigation';
import { ErrorService } from '@/core/shared/errors/error.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import ProjectService from '@/core/shared/project/project.service';
import {
  ApplicationBulkActions,
  type BulkChangeApplicationStatusResult,
} from '@/core/conversations/conversation-index/conversation-index.type';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import { JobApplicantStatus, type Project } from '@factoryfixinc/ats-interfaces';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import ProNotesService from '@/core/shared/pro-notes/pro-notes.service';
import { PromisePoolAllSettledStatus } from '@/utils/promise.util';
import { type CandidateExportInfo, exportXlsx } from '@/utils/export-xlsx-utils';
import { DialogService } from '@/core/shared/dialog/dialog.service';
import { retry } from 'radash';
import { ConversationIndexPersistence } from '@/core/conversations/conversation-index/conversation-index.persistence';
import ApplicationScoreService from '@/core/conversations/application-score/application-score.service';
import CopilotScoreLearningBanner from '../Copilot/CopilotScoreLearningBanner.vue';

const INFINITE_SCROLL_ID = 'conversation-infinite-scroll-element';

const dialogService = new DialogService();
const projectService = new ProjectService();
const applicationScoreService = new ApplicationScoreService();
const conversationIndexService = new ConversationIndexService();
const proProfileService = new ProProfileService();
const proNotesService = new ProNotesService();
const conversationIndexPersistence = new ConversationIndexPersistence();
const route = useRoute();
const router = useRouter();

const isSendingBulkAction = ref(false);
const rejectedModalVisible = ref(false);

const currentProject = computed(() => {
  return projectService.currentProject;
});

const currentProjectJobId = computed(() => projectService.currentProject?.jobId);

// `currentProject` can be unset here even though the list is scoped to a job: the jobs
// route loads the conversation list before it selects the project, so an aborted or
// swallowed route change can leave one set and the other not. Fall back to the job the
// list is scoped to so bulk actions still know which job they are acting on.
const resolvedJobId = computed(
  () => currentProjectJobId.value ?? conversationIndexService.conversationIndexSearch.jobIds?.[0],
);

const conversationIndexes = computed(() => conversationIndexService.conversationIndexes);

const hasConversations = computed(() => conversationIndexes.value.length > 0);

const loadingConversationIndexes = computed(
  () => conversationIndexService.loadingConversationIndexes,
);

const loadingMoreConversations = computed(() => conversationIndexService.loadingMoreConversations);

const selectedConversationIndex = computed(
  () => conversationIndexService.selectedConversationIndex,
);

const areAllConversationsSelected = computed(() => {
  if (!conversationIndexes.value.length) return false;
  return conversationIndexes.value.every((conversationIndex) => conversationIndex.isBulkSelection);
});

const areSomeConversationsSelected = computed(() => {
  return conversationIndexes.value.some((conversationIndex) => conversationIndex.isBulkSelection);
});

const { resetInfiniteScroll } = useConversationListInfiniteScroll(INFINITE_SCROLL_ID);

// The scrolling list under the fixed header. Its scrolled state drives the header hairline, and any
// overflow (a scrollbar is showing) drives the right gutter.
const listScrollRef = ref<HTMLElement | null>(null);
const listContentRef = ref<HTMLElement | null>(null);
const { hasContentAbove: isListScrolled, hasContentBelow } = useScrollFades(
  listScrollRef,
  listContentRef,
);
const isListOverflowing = computed(() => isListScrolled.value || hasContentBelow.value);

// Account-wide candidate search (ONE-6912 "Jump to any candidate"):
// search-mode state, two-zone partitioning, scroll preservation, and result
// selection all live in the composable.
const {
  currentJobTitle,
  globalSearchHasHiddenRows,
  globalSearchHasMore,
  globalSearchLoadedCandidateCount,
  globalSearchPartition,
  globalSearchQuery,
  handleGlobalSearchRetry,
  handleGlobalSearchSelect,
  isGlobalSearchActive,
  isGlobalSearchError,
  isGlobalSearchLoading,
} = useGlobalCandidateSearch(listScrollRef);

const bannerTime = ref(Date.now());
let bannerTimeInterval: ReturnType<typeof setInterval>;

const showAiLearningBanner = computed<boolean>(() => {
  if (!currentProjectJobId.value) return false;

  return (
    hasConversations.value &&
    applicationScoreService.shouldShowAiLearningBanner(currentProjectJobId.value, bannerTime.value)
  );
});

const isBelowOrAboveSelectedConversation = (index: number): boolean => {
  if (!selectedConversationIndex.value || !conversationIndexes.value) return false;

  return [
    conversationIndexes.value[index]?.conversationId,
    conversationIndexes.value[index + 1]?.conversationId,
  ].includes(selectedConversationIndex.value?.conversationId);
};

function scrollSelectedConversationIntoView() {
  const selectedConversationId = selectedConversationIndex.value?.conversationId;

  if (!selectedConversationId) {
    return;
  }

  const selectedCardElement = document.getElementById(
    `conversation-index-card-${selectedConversationId}`,
  );

  selectedCardElement?.scrollIntoView({
    behavior: 'instant',
    block: 'center',
    inline: 'nearest',
  });
}

const handleSelectAll = (isSelectAll: boolean) => {
  conversationIndexService.toggleBulkActionAllConversationIndexes(isSelectAll);
};

const handleBulkAction = async (action: ApplicationBulkActions) => {
  if (isSendingBulkAction.value) return;

  if (!areAllConversationsSelected.value && !areSomeConversationsSelected.value) {
    SnackbarService.caution('Please select at least one conversation to perform this action.');
    return;
  }
  if (action === ApplicationBulkActions.MOVE_TO_REJECTED_W_REASON) {
    rejectedModalVisible.value = true;
    return;
  }

  if (action === ApplicationBulkActions.BULK_MESSAGE) {
    openBulkMessagingModal();
    return;
  }

  if (action === ApplicationBulkActions.EXPORT_CANDIDATES) {
    exportCandidates();
    return;
  }

  if (action === ApplicationBulkActions.ADD_TO_ANOTHER_JOB) {
    addCandidatesToAnotherJob();
    return;
  }

  changeBulkStatus(action);
};

const handleRejectedReason = (reason: string, isSilent: boolean) => {
  if (!reason) {
    SnackbarService.caution('Please provide a reason for rejection.');
    return;
  }
  rejectedModalVisible.value = false;
  changeBulkStatus(ApplicationBulkActions.MOVE_TO_REJECTED_W_REASON, isSilent, reason);
};

const changeBulkStatus = async (
  action: ApplicationBulkActions,
  isSilent: boolean = true,
  reason: string = '',
) => {
  let bulkResult: BulkChangeApplicationStatusResult | undefined = undefined;

  isSendingBulkAction.value = true;
  TrackingService.trackAction(TrackingActionName.BULK_ACTION_CHANGE_CONVERSATION_STATUS, {
    source: 'Conversations Index Filters',
    action,
    reason,
    isSilent,
  });

  try {
    SnackbarService.caution('Processing bulk action...');

    const prosToUpdate = await conversationIndexService.getBulkConversationIndexes();

    if (!prosToUpdate) {
      return;
    }

    const applicationIds = prosToUpdate.map((pro) => pro.applicationId);

    bulkResult = await proProfileService.bulkChangeApplicationStatus(
      applicationIds,
      action,
      isSilent,
      reason,
    );

    if (!bulkResult || bulkResult.successCount === 0) {
      throw new Error('Bulk action failed');
    }

    if (action === ApplicationBulkActions.MOVE_TO_REJECTED_W_REASON) {
      // We only add notes for the ones that were successfully updated
      const prosIdsToUpdate: number[] = [];
      bulkResult.result.forEach((result, index) => {
        if (result.status === PromisePoolAllSettledStatus.Fulfilled) {
          prosIdsToUpdate.push(prosToUpdate[index].proProfileId);
        }
      });

      if (prosIdsToUpdate.length > 0) {
        await proNotesService.bulkCreateProsRejectedNote(prosIdsToUpdate, reason);
      }
    }

    if (bulkResult.errorCount > 0) {
      SnackbarService.caution(
        `Bulk action complete, but ${bulkResult.errorCount} of
        ${
          bulkResult.errorCount + bulkResult.successCount
        } failed to change status. <br />Please try these individually.`,
      );
    } else {
      SnackbarService.success('Processing bulk action completed!');
    }
    // Refresh the candidates list and chip counts
    const statusList = conversationIndexService.selectedStatusList || [JobApplicantStatus.CLIENT];

    // First refresh the project to get updated counts. Skip it when no project is
    // selected — the status change has already succeeded, and an unchecked id here
    // requested `/project/undefined/employer/<id>`.
    if (currentProject.value?.id) {
      await projectService.getProjectById(currentProject.value.id);
    }

    // Then update the conversation search with proper filters
    const refreshJobId = resolvedJobId.value;
    if (refreshJobId) {
      await conversationIndexService.updateConversationIndexSearch(
        {
          applicationStatus: statusList,
          jobIds: [refreshJobId],
        },
        { resetPagination: true, doNotSelectFirst: true },
      );

      // If the previously-selected conversation was part of the bulk move and is no longer
      // in the refreshed list, advance the right pane to the new top-of-list (or empty state).
      // Snackbar is suppressed (movedConversationId/newStatus omitted) because the bulk
      // flow already shows its own `Processing bulk action completed!` toast above.
      const advanceResult = conversationIndexService.advanceSelectedConversationIfMissing();
      if (currentProject.value?.id) {
        await navigateAfterConversationStatusChange({
          router,
          route,
          projectId: currentProject.value.id,
          advanceResult,
        });
      }
    }

    isSendingBulkAction.value = false;
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not perform bulk action. Please try again later.');
    isSendingBulkAction.value = false;
  }
};

const exportCandidates = async () => {
  // Bail out rather than calling `/job/undefined/job-applicant`.
  const exportJobId = resolvedJobId.value;

  if (!exportJobId) {
    SnackbarService.caution('Please select a job before exporting candidates.');
    return;
  }

  SnackbarService.caution('Processing export...');
  const bulkConversationIndexes = await conversationIndexService.getBulkConversationIndexes();
  const selectedApplicantIds =
    bulkConversationIndexes?.map((applicant) => applicant.applicationId) ?? [];

  TrackingService.trackAction(TrackingActionName.BULK_ACTION_EXPORT_APPLICANTS, {
    source: 'Conversations Index Filters',
    applicantsQty: selectedApplicantIds?.length,
  });

  try {
    const applicants = await proProfileService.getApplicantsByApplicationId(
      exportJobId,
      selectedApplicantIds,
    );

    if ((applicants ?? []).length === 0) {
      SnackbarService.caution('No candidates selected for export.');
      return;
    }

    const fileName = !currentProject.value
      ? 'Candidates'
      : `FactoryFix ${currentProject.value.title} Candidates`;

    const exportInfo = {
      candidateData: applicants,
      jobId: exportJobId,
      fileName: fileName,
      sheetName: 'Candidates',
    } as CandidateExportInfo;
    exportXlsx(exportInfo);
    SnackbarService.success('Export complete!');
  } catch (error) {
    ErrorService.captureException(error);
    SnackbarService.critical('Could not export candidates. Please try again later.');
  }
};

async function addCandidatesToAnotherJob() {
  const bulkConversationIndexes = await conversationIndexService.getBulkConversationIndexes();

  const selectedUserProfiles =
    bulkConversationIndexes?.map((conversationIndex) => ({ id: conversationIndex.proId })) ?? [];

  dialogService.addProToJobModalProps = {
    isOpen: true,
    pros: selectedUserProfiles,

    additionalActionsBeforeSubmit: async (project: Project) => {
      /**
       * Once an application is created in ff-rest, it takes a few seconds to sync
       * with the conversations' database. This retry logic checks that each application
       * is synced successfully before continuing.
       */
      await retry({ times: 5, delay: 3000 }, async () => {
        const results = await conversationIndexPersistence.fetchConversationIndexes({
          employerId: project.employerId,
          params: {
            jobIds: [project.jobId],
            onlyCurrentUser: false,
            onlyUnread: false,
            pagination: {
              page: 1,
              itemsPerPage: 100,
            },
          },
        });

        const areConversationIndexesSynced = selectedUserProfiles.every((profile) => {
          return results.find((index) => index.proId === profile.id);
        });

        if (!areConversationIndexesSynced) {
          throw new Error('Conversation indexes are not synced');
        }
      });

      /**
       * We will move the user to the job they moved the candidate(s) to. So we
       * should set the selected status to "New" to ensure the newly added
       * applications are visible.
       */
      const status = [JobApplicantStatus.NEW];
      conversationIndexService.resetConversationIndexes();
      conversationIndexService.selectedStatusList = status;
      conversationIndexService.selectedStatusFilters = status;

      await conversationIndexService.updateConversationIndexSearch(
        {
          applicationStatus: status,
          jobIds: [project.jobId],
        },
        { resetPagination: true },
      );

      await projectService.getProjectById(project.id);
    },
  };
}

async function openBulkMessagingModal() {
  const conversationIndexes = await conversationIndexService.getBulkMessageConversationIndexes();
  dialogService.bulkMessagingModalProps = {
    isOpen: true,
    conversationIndexes,
  };
}

function onBulkMessagingModalClose() {
  dialogService.bulkMessagingModalProps.isOpen = false;
}

function handleStatusFilterUpdate() {
  resetInfiniteScroll();
}

onMounted(() => {
  scrollSelectedConversationIntoView();
  bannerTimeInterval = setInterval(() => {
    bannerTime.value = Date.now();
  }, 10000);
});

onBeforeUnmount(() => {
  if (bannerTimeInterval) {
    clearInterval(bannerTimeInterval);
  }
});
</script>

<template>
  <div class="relative flex h-full max-h-screen w-full flex-col pt-4">
    <!-- Fixed header: title row, stage pills and select-all/sort row stay put while the
         list below scrolls. The hairline reaches the column edges (-inset-x-2 cancels the
         section's px-2) and only shows once cards have scrolled under it. -->
    <div class="relative shrink-0 space-y-3 pb-3" data-test="candidates-header">
      <!-- Search and status filters -->
      <ConversationIndexSearchFilter />

      <!-- While an account-wide search is active, browsing controls are
         suspended (not hidden, not reset): their saved state is untouched
         and they come back the moment the query clears. `inert` removes the
         whole region from pointer AND keyboard/AT interaction — class-based
         pointer-events alone left descendants Tab-reachable. -->
      <div
        :inert="isGlobalSearchActive || undefined"
        :class="{ 'pointer-events-none opacity-40': isGlobalSearchActive }"
        :aria-disabled="isGlobalSearchActive || undefined"
        data-test="status-filter-wrapper"
      >
        <ConversationIndexStatusFilter
          :project="currentProject"
          @update:status="handleStatusFilterUpdate"
        />
      </div>

      <!-- Bulk actions and filters. Also explicitly disabled: their menus
         teleport outside this wrapper, so `inert` on the wrapper alone would
         not cover an already-open overlay. -->
      <div
        class="flex items-center"
        :inert="isGlobalSearchActive || undefined"
        :class="{ 'pointer-events-none opacity-40': isGlobalSearchActive }"
        :aria-disabled="isGlobalSearchActive || undefined"
        data-test="bulk-options-wrapper"
      >
        <ConversationBulkOptions
          :are-all-selected="areAllConversationsSelected"
          :are-some-selected="areSomeConversationsSelected"
          :is-sending-bulk-action="isSendingBulkAction"
          :disabled="isGlobalSearchActive"
          @on-select-all="handleSelectAll"
          @on-bulk-action="handleBulkAction"
          class="mb-2 mr-1 flex shrink-0 self-center pl-2"
        />
        <ConversationIndexPopoverFilter class="flex-grow" :disabled="isGlobalSearchActive" />
      </div>

      <div
        class="pointer-events-none absolute -inset-x-2 bottom-0 h-px bg-tint-80 transition-opacity duration-200"
        :class="isListScrolled ? 'opacity-100' : 'opacity-0'"
        :data-scrolled="isListScrolled"
        data-test="candidates-header-divider"
      />
    </div>

    <!-- Scrolling region. -mx-2 spans the panel edge to edge. The cards keep 8px on the right
         until a scrollbar appears; the snug 12px bar then leaves 2px either side of its 8px thumb. -->
    <div
      ref="listScrollRef"
      class="menu-scrollbar menu-scrollbar--snug -mx-2 min-h-0 flex-1 overflow-y-auto pl-2"
      :class="{ 'pr-2': !isListOverflowing }"
      :data-overflowing="isListOverflowing"
      data-test="candidates-list-scroll"
    >
      <div ref="listContentRef">
        <!-- Two-zone account-wide search results (ONE-6912) -->
        <GlobalCandidateSearchResults
          v-if="isGlobalSearchActive"
          :query="globalSearchQuery"
          :current-job-title="currentJobTitle"
          :partition="globalSearchPartition"
          :loading="isGlobalSearchLoading"
          :error="isGlobalSearchError"
          :has-hidden-rows="globalSearchHasHiddenRows"
          :has-more="globalSearchHasMore"
          :loaded-candidate-count="globalSearchLoadedCandidateCount"
          @select="handleGlobalSearchSelect"
          @retry="handleGlobalSearchRetry"
        />

        <!-- Conversation list. v-show (not v-if) so the resting list, its scroll
         sentinel, and the infinite-scroll observer survive search mode. -->
        <div v-show="!isGlobalSearchActive" class="flex w-full flex-col">
          <!-- Show loader cards only on initial load when there are no conversations -->
          <template v-if="loadingConversationIndexes && !hasConversations">
            <ConversationIndexLoaderCards />
          </template>
          <!-- Show conversations if we have any, regardless of loading state -->
          <template v-else-if="hasConversations">
            <!-- AI Learning Banner (original position: inside conversation list) -->
            <Transition name="slide-fade">
              <CopilotScoreLearningBanner
                v-if="showAiLearningBanner"
                :job-id="currentProjectJobId!"
                class="mb-3"
              />
            </Transition>
            <div
              v-for="(conversationIndex, index) in conversationIndexes"
              :key="conversationIndex.conversationId"
            >
              <ConversationIndexCard
                :conversationIndex="conversationIndex"
                @on-update-check="
                  (checkValue: boolean) => (conversationIndex.isBulkSelection = checkValue)
                "
              />
              <div
                v-if="index !== conversationIndexes.length - 1"
                :class="{
                  'border-white': isBelowOrAboveSelectedConversation(index),
                  'border-tint-60': !isBelowOrAboveSelectedConversation(index),
                }"
                class="border-b"
              />
            </div>
          </template>
          <!-- Show empty results only when not loading and no conversations -->
          <template v-else>
            <ConversationIndexFilterEmptyResults />
          </template>

          <!-- Keep this element with a fixed height so that the infinite scroll observer works
       If it has a height of 0px, the observer will not work -->
          <div :id="INFINITE_SCROLL_ID" class="mb-2 mt-8 flex h-8 items-center justify-center">
            <SpinnerLoader v-show="loadingMoreConversations" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Rejected modal -->
  <RejectedModal v-model="rejectedModalVisible" @on-update-rejected-reason="handleRejectedReason" />
  <!-- Bulk messaging modal -->
  <BulkMessagingModal @close="onBulkMessagingModalClose" />
</template>

<style scoped>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
  @apply -translate-y-full opacity-0;
}

.slide-fade-leave-to {
  @apply -translate-y-full opacity-0;
}
</style>
